import React, { useState, useEffect } from 'react';
import { fetchShippingToday } from '../services/api';

function toApiDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
}

const todayIso = new Date().toISOString().split('T')[0];
const PAGE_SIZE = 200;

export default function ShippingReport({ navigateTo }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [date, setDate] = useState(todayIso);

  const load = (d) => {
    setLoading(true);
    setError(null);
    fetchShippingToday(toApiDate(d))
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(date); }, []);

  const orders = data?.orders ?? [];
  const header = data?.header ?? null;
  const totalRevenue = orders.reduce((s, o) => s + parseFloat((o.total || '0').replace('$', '') || 0), 0);
  const totalProfit = orders.reduce((s, o) => s + Number(o.profit ?? 0), 0);

  const byShipType = orders.reduce((acc, o) => {
    const t = o.shipType || 'Unknown';
    if (!acc[t]) acc[t] = { count: 0, revenue: 0 };
    acc[t].count++;
    acc[t].revenue += parseFloat((o.total || '0').replace('$', '') || 0);
    return acc;
  }, {});

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Shipping Report</h2>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header">Date</div>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>SHIP DATE</div>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} className="form-input" style={{ fontSize: 11 }} />
          </div>
          <button onClick={() => load(date)} className="btn btn-primary" style={{ fontSize: 11 }}>Search</button>
        </div>
      </div>

      {header && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12 }}>
          {[
            { label: 'Total Orders', value: orders.length.toLocaleString() },
            { label: 'Revenue', value: `$${totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
            { label: 'Total Profit', value: `$${totalProfit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, highlight: true },
            { label: 'Avg Profit', value: orders.length ? `$${(totalProfit / orders.length).toFixed(2)}` : '$0.00', highlight: true },
          ].map(({ label, value, highlight }) => (
            <div key={label} className="card" style={{ padding: '10px 14px' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: highlight ? 'var(--orange)' : 'var(--navy)' }}>{value}</div>
            </div>
          ))}
        </div>
      )}

      {Object.keys(byShipType).length > 0 && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div className="card-header">By Ship Type</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Ship Type</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Orders</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(byShipType).sort((a, b) => b[1].count - a[1].count).map(([type, stats], i) => (
                  <tr key={type} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)' }}>
                    <td style={{ padding: '8px 12px', color: 'var(--navy)', fontWeight: 600 }}>{type}</td>
                    <td style={{ padding: '8px 8px', textAlign: 'right', color: 'var(--orange)', fontWeight: 700 }}>{stats.count}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>${stats.revenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">Order Detail {orders.length > 0 && `(${orders.length})`}</div>
        {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
        {error && <div style={{ padding: 16, fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
        {!loading && !error && orders.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No shipping orders for selected date.</div>
        )}
        {orders.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Order ID</th>
                  <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Customer</th>
                  <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Ship Type</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Total</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Profit</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)', cursor: navigateTo ? 'pointer' : 'default' }}
                    onClick={() => navigateTo && navigateTo('order-details', order.id)}>
                    <td style={{ padding: '8px 12px', color: 'var(--orange)', fontWeight: 600 }}>{order.id}</td>
                    <td style={{ padding: '8px 8px', color: 'var(--navy)' }}>{order.name}</td>
                    <td style={{ padding: '8px 8px', color: 'var(--gray-700)' }}>{order.shipType}</td>
                    <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 600 }}>{order.total}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: order.profit >= 0 ? 'var(--navy)' : 'var(--red)' }}>
                      ${Number(order.profit ?? 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
